import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PageLayout, ActionButton } from "../../../components/modern";
import { useAuth } from "../../../contexts/AuthContext";
import { adminApi } from "../../../utils/api";
import { showSuccessToast, showErrorToast } from "../../../utils/toastNotifications";
import {
  HiArrowLeft,
  HiPencil,
  HiPrinter,
  HiRefresh,
  HiDocumentText,
  HiClipboardList,
  HiChartBar,
  HiCog
} from "react-icons/hi";
import "./OrderDetails.scss";

interface Order {
  id: number;
  orderGroup: string;
  user: {
    id: number;
    name: string;
    email: string;
    phone: string;
  };
  merchantName: string;
  description: string;
  quantity: number;
  orderStatus: number;
  totalPrice: number;
  voucherValue: number;
  weight: number;
  width: number;
  height: number;
  length: number;
  trackingId?: string;
  itemPrice: number;
  servicePrice: number;
  discount: number;
  extraService: number;
  totalPaid: number;
  netDue: number;
  merchantDeliveryFee: number;
  currency: string;
  createdAt: string;
  updatedAt: string;
  // Additional fields from original
  link: string;
  extendedDescription: string;
  destinationCountryId: number;
  originCountryId: number;
  paymentMethod: number;
  categoryId: number;
  shippingCompanyId: number;
  merchentDeliveryFee: number;
  itemType: number;
  deliveryMode: number;
  paymentMethodId: number;
  paymentType: number;
  additionaCost: number;
  shippingCompanyPricePerLB: number;
  ShippingCompanyId: number;
  currencyId: number;
  orderPrice: number;
  exchangePrice: number;
  diementionalWeight: number;
  shippingCost: number;
  shippingInssurance: number;
  shippingFuelCost: number;
  tax: number;
  buyForMeCost: number;
  paymentMethodCost: number;
  refundType: number;
  hasShippingInssurance: boolean;
  invoives: any[];
  orderLogs: any[];
}

interface OrderLog {
  id: number;
  action: string;
  description: string;
  userId: number;
  userName: string;
  createdAt: string;
}

export const OrderDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { token, hasPermission } = useAuth();
  const { t, i18n } = useTranslation('orders');
  const isRTL = i18n.language === 'ar';

  const [order, setOrder] = useState<Order | null>(null);
  const [logs, setLogs] = useState<OrderLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('details');

  // Edit form state
  const [editFormData, setEditFormData] = useState<any>({});
  const [countries, setCountries] = useState<any[]>([]);
  const [currencies, setCurrencies] = useState<any[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [shippingCompanies, setShippingCompanies] = useState<any[]>([]);
  const [updating, setUpdating] = useState(false);

  // Status update form
  const [orderStatusForm, setOrderStatusForm] = useState<any>({
    orderStatus: 0,
    refundType: 0,
    comment: "",
    orderId: id,
    extraField: "",
  });
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const fetchOrder = async () => {
    try {
      setLoading(true);
      const response = await adminApi.call(`/adminorder/${id}`, 'GET');

      if (!response.success) {
        showErrorToast(response.error || t('messages.fetchError'));
        return;
      }

      setOrder(response.data);
    } catch (error) {
      console.error(error);
      showErrorToast(t('messages.fetchError'));
    } finally {
      setLoading(false);
    }
  };

  const fetchLogs = async () => {
    try {
      const response = await adminApi.call(`/adminorder/${id}/logs`, 'GET');

      if (response.success) {
        setLogs(response.data?.items || []);
      }
    } catch (error) {
      console.error(error);
    }
  };

  // Fetch dropdown data for edit form
  const fetchDropdownData = async () => {
    try {
      const [countriesRes, currenciesRes, paymentMethodsRes, categoriesRes, shippingCompaniesRes] = await Promise.all([
        adminApi.call('/country', 'GET'),
        adminApi.call('/GetBothAndSourceCurrencies', 'GET'),
        adminApi.call('/paymentmethod', 'GET'),
        adminApi.call('/category', 'GET'),
        adminApi.call('/shippingcompany?pageSize=10000', 'GET')
      ]);

      if (countriesRes.success) {
        setCountries(countriesRes.data?.items?.filter((x: any) => !x.isSuspended) || []);
      }
      if (currenciesRes.success) {
        setCurrencies(currenciesRes.data?.items || []);
      }
      if (paymentMethodsRes.success) {
        setPaymentMethods(paymentMethodsRes.data?.items || []);
      }
      if (categoriesRes.success) {
        setCategories(categoriesRes.data?.items || []);
      }
      if (shippingCompaniesRes.success) {
        setShippingCompanies(shippingCompaniesRes.data?.items || []);
      }
    } catch (error) {
      console.error('Error fetching dropdown data:', error);
    }
  };

  // Re-fetch shipping companies when origin or destination country changes in edit form
  useEffect(() => {
    if (editFormData.originCountryId || editFormData.destinationCountryId) {
      fetchShippingCompaniesFiltered();
    }
  }, [editFormData.originCountryId, editFormData.destinationCountryId]);

  const fetchShippingCompaniesFiltered = async () => {
    try {
      // Build query parameters with filters
      const params = new URLSearchParams();
      params.append('pageSize', '10000');

      if (editFormData.originCountryId && editFormData.originCountryId > 0) {
        params.append('sourceCountryId', editFormData.originCountryId.toString());
      }

      if (editFormData.destinationCountryId && editFormData.destinationCountryId > 0) {
        params.append('destinationCountryId', editFormData.destinationCountryId.toString());
      }

      const shippingCompaniesRes = await adminApi.call(`/shippingcompany?${params.toString()}`, 'GET');
      if (shippingCompaniesRes.success) {
        setShippingCompanies(shippingCompaniesRes.data?.items || []);
      }
    } catch (error) {
      console.error('Error fetching filtered shipping companies:', error);
    }
  };

  useEffect(() => {
    if (id) {
      fetchOrder();
      fetchLogs();
      fetchDropdownData();
    }
  }, [id]);

  // Initialize edit form when order data is loaded
  useEffect(() => {
    if (order) {
      setEditFormData({ ...order });
      setOrderStatusForm(prev => ({
        ...prev,
        orderStatus: order.orderStatus
      }));
    }
  }, [order]);

  const getStatusBadge = (status: number) => {
    const statusColorMap: { [key: number]: string } = {
      0: "secondary", 1: "info", 2: "danger", 3: "primary", 4: "danger",
      5: "secondary", 6: "success", 7: "success", 8: "danger", 9: "warning",
      10: "warning", 11: "dark", 12: "danger", 13: "info", 14: "primary",
      15: "success", 16: "danger", 17: "warning", 18: "info", 19: "secondary",
      20: "primary", 21: "success", 22: "warning", 23: "info", 24: "primary",
      25: "success", 26: "danger", 27: "warning", 28: "danger"
    };

    const statusText = t(`statusOptions.${status}`, { defaultValue: "Unknown" });
    const statusColor = statusColorMap[status] || "secondary";

    return (
      <span className={`badge bg-${statusColor}`}>{statusText}</span>
    );
  };

  const handleEditOrder = () => {
    navigate(`/order/edit/${id}`);
  };

  const handlePrintInvoice = () => {
    navigate(`/order/print/${id}`);
  };

  const handleBack = () => {
    navigate('/order');
  };

  // Form handlers
  const handleEditInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setEditFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (type === 'number' ? parseFloat(value) || 0 : value)
    }));
  };

  const handleStatusFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setOrderStatusForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleUpdateOrder = async () => {
    if (!hasPermission("Edit Order")) {
      showErrorToast("You don't have permission to edit orders");
      return;
    }

    try {
      setUpdating(true);
      const response = await fetch(`${import.meta.env.VITE_APP_API_BASE_URL}/adminorder/${id}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(editFormData)
      });

      if (response.ok) {
        showSuccessToast("Order updated successfully");
        fetchOrder(); // Refresh order data
        setActiveTab('details'); // Switch back to details tab
      } else {
        throw new Error("Failed to update order");
      }
    } catch (error) {
      showErrorToast("Error updating order: " + error.message);
    } finally {
      setUpdating(false);
    }
  };

  const handleUpdateStatus = async () => {
    try {
      setUpdatingStatus(true);
      const response = await fetch(`${import.meta.env.VITE_APP_API_BASE_URL}/adminorder/status`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(orderStatusForm)
      });

      if (response.ok) {
        showSuccessToast("Order status updated successfully");
        fetchOrder(); // Refresh order data
        fetchLogs(); // Refresh logs
      } else {
        throw new Error("Failed to update order status");
      }
    } catch (error) {
      showErrorToast("Error updating status: " + error.message);
    } finally {
      setUpdatingStatus(false);
    }
  };

  if (loading) {
    return (
      <PageLayout title={t('loading')} breadcrumbs={[]}>
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('loading')}</span>
          </div>
        </div>
      </PageLayout>
    );
  }

  if (!order) {
    return (
      <PageLayout title={t('orderDetails')} breadcrumbs={[]}>
        <div className="text-center py-5">
          <div className="alert alert-danger">
            {t('messages.orderNotFound')}
          </div>
          <ActionButton
            variant="primary"
            onClick={handleBack}
            icon={HiArrowLeft}
          >
            {t('actions.back')}
          </ActionButton>
        </div>
      </PageLayout>
    );
  }

  const breadcrumbs = [
    { label: t('breadcrumbs.orders'), path: '/order' },
    { label: `${t('breadcrumbs.view')} #${order.id}` }
  ];

  const headerActions = (
    <div className="d-flex gap-2">
      <ActionButton
        variant="info"
        onClick={() => fetchOrder()}
        icon={HiRefresh}
      >
        {t('actions.refresh')}
      </ActionButton>
      {hasPermission("Edit Order") && (
        <ActionButton
          variant="warning"
          onClick={handleEditOrder}
          icon={HiPencil}
        >
          {t('actions.edit')}
        </ActionButton>
      )}
      <ActionButton
        variant="primary"
        onClick={handlePrintInvoice}
        icon={HiPrinter}
      >
        {t('actions.print')}
      </ActionButton>
    </div>
  );

  return (
    <PageLayout
      title={`${t('orderDetails')} #${order.id}`}
      subtitle={`${t('fields.customer')}: ${order.user?.name}`}
      breadcrumbs={breadcrumbs}
      headerActions={headerActions}
      className={`order-details ${isRTL ? 'rtl' : 'ltr'}`}
    >
      {/* Order Status Header */}
      <div className="order-status-header mb-4">
        <div className="row align-items-center">
          <div className="col-md-6">
            <div className="status-info">
              <span className="status-label">{t('fields.status')}:</span>
              {getStatusBadge(order.orderStatus)}
            </div>
          </div>
          <div className="col-md-6 text-md-end">
            <div className="order-meta">
              <small className="text-muted">
                {t('fields.createdAt')}: {new Date(order.createdAt).toLocaleDateString()}
              </small>
            </div>
          </div>
        </div>
      </div>

      {/* Tabbed Interface */}
      <div className="order-tabs-container">
        <ul className="nav nav-tabs nav-tabs-modern" role="tablist">
          <li className="nav-item" role="presentation">
            <button
              className={`nav-link ${activeTab === 'details' ? 'active' : ''}`}
              onClick={() => setActiveTab('details')}
              type="button"
              role="tab"
            >
              <HiDocumentText className="me-2" />
              {t('sections.orderInfo')}
            </button>
          </li>
          {hasPermission("Edit Order") && (
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === 'edit' ? 'active' : ''}`}
                onClick={() => setActiveTab('edit')}
                type="button"
                role="tab"
              >
                <HiPencil className="me-2" />
                {t('tabs.editOrder')}
              </button>
            </li>
          )}
          <li className="nav-item" role="presentation">
            <button
              className={`nav-link ${activeTab === 'status' ? 'active' : ''}`}
              onClick={() => setActiveTab('status')}
              type="button"
              role="tab"
            >
              <HiCog className="me-2" />
              {t('tabs.statusUpdate')}
            </button>
          </li>
          <li className="nav-item" role="presentation">
            <button
              className={`nav-link ${activeTab === 'invoice' ? 'active' : ''}`}
              onClick={() => setActiveTab('invoice')}
              type="button"
              role="tab"
            >
              <HiClipboardList className="me-2" />
              {t('tabs.invoice')}
            </button>
          </li>
          <li className="nav-item" role="presentation">
            <button
              className={`nav-link ${activeTab === 'logs' ? 'active' : ''}`}
              onClick={() => setActiveTab('logs')}
              type="button"
              role="tab"
            >
              <HiClipboardList className="me-2" />
              {t('tabs.logs')}
            </button>
          </li>
          <li className="nav-item" role="presentation">
            <button
              className={`nav-link ${activeTab === 'summaries' ? 'active' : ''}`}
              onClick={() => setActiveTab('summaries')}
              type="button"
              role="tab"
            >
              <HiChartBar className="me-2" />
              {t('tabs.summaries')}
            </button>
          </li>
        </ul>

        <div className="tab-content">
          {/* Order Details Tab */}
          {activeTab === 'details' && (
            <div className="tab-pane active">
              <div className="row g-4">
                {/* Customer Information */}
                <div className="col-lg-6">
                  <div className="info-card">
                    <h5 className="info-card-title">{t('sections.customerInfo')}</h5>
                    <div className="info-grid">
                      <div className="info-item">
                        <span className="label">{t('fields.customer')}:</span>
                        <span className="value">{order.user?.name}</span>
                      </div>
                      <div className="info-item">
                        <span className="label">{t('fields.phone')}:</span>
                        <span className="value">{order.user?.phone || 'N/A'}</span>
                      </div>
                      <div className="info-item">
                        <span className="label">Email:</span>
                        <span className="value">{order.user?.email || 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Merchant Information */}
                <div className="col-lg-6">
                  <div className="info-card">
                    <h5 className="info-card-title">{t('sections.merchantDetails')}</h5>
                    <div className="info-grid">
                      <div className="info-item">
                        <span className="label">{t('fields.merchantName')}:</span>
                        <span className="value">{order.merchantName}</span>
                      </div>
                      <div className="info-item">
                        <span className="label">{t('fields.description')}:</span>
                        <span className="value">{order.description}</span>
                      </div>
                      <div className="info-item">
                        <span className="label">{t('fields.orderGroup')}:</span>
                        <span className="value">{order.orderGroup}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Order Information */}
                <div className="col-lg-6">
                  <div className="info-card">
                    <h5 className="info-card-title">{t('sections.orderInfo')}</h5>
                    <div className="info-grid">
                      <div className="info-item">
                        <span className="label">{t('fields.quantity')}:</span>
                        <span className="value">
                          <span className="badge bg-primary">{order.quantity}</span>
                        </span>
                      </div>
                      <div className="info-item">
                        <span className="label">{t('fields.weight')}:</span>
                        <span className="value">{order.weight} {t('units.lb')}</span>
                      </div>
                      <div className="info-item">
                        <span className="label">{t('fields.trackingId')}:</span>
                        <span className="value">{order.trackingId || 'N/A'}</span>
                      </div>
                      <div className="info-item">
                        <span className="label">{t('common:productLink')}:</span>
                        <span className="value">
                          {order.link ? (
                            <a href={order.link} target="_blank" rel="noopener noreferrer" className="text-primary">
                              View Product
                            </a>
                          ) : 'N/A'}
                        </span>
                      </div>
                      <div className="info-item">
                        <span className="label">Extended Description:</span>
                        <span className="value">{order.extendedDescription || 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Dimensions */}
                <div className="col-lg-6">
                  <div className="info-card">
                    <h5 className="info-card-title">{t('sections.dimensions')}</h5>
                    <div className="info-grid">
                      <div className="info-item">
                        <span className="label">{t('fields.width')}:</span>
                        <span className="value">{order.width} {t('units.inch')}</span>
                      </div>
                      <div className="info-item">
                        <span className="label">{t('fields.height')}:</span>
                        <span className="value">{order.height} {t('units.inch')}</span>
                      </div>
                      <div className="info-item">
                        <span className="label">{t('fields.length')}:</span>
                        <span className="value">{order.length} {t('units.inch')}</span>
                      </div>
                      <div className="info-item">
                        <span className="label">Dimensional Weight:</span>
                        <span className="value">{order.diementionalWeight} {t('units.lb')}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Shipping Information */}
                <div className="col-lg-6">
                  <div className="info-card">
                    <h5 className="info-card-title">{t('sections.shippingTracking')}</h5>
                    <div className="info-grid">
                      <div className="info-item">
                        <span className="label">Shipping Cost:</span>
                        <span className="value">${order.shippingCost?.toFixed(2) || '0.00'}</span>
                      </div>
                      <div className="info-item">
                        <span className="label">Shipping Insurance:</span>
                        <span className="value">${order.shippingInssurance?.toFixed(2) || '0.00'}</span>
                      </div>
                      <div className="info-item">
                        <span className="label">Shipping Fuel Cost:</span>
                        <span className="value">${order.shippingFuelCost?.toFixed(2) || '0.00'}</span>
                      </div>
                      <div className="info-item">
                        <span className="label">Shipment Insurance:</span>
                        <span className="value">
                          <span className={`badge ${order.hasShippingInssurance ? 'bg-success' : 'bg-secondary'}`}>
                            {order.hasShippingInssurance ? 'Yes' : 'No'}
                          </span>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Additional Costs */}
                <div className="col-lg-6">
                  <div className="info-card">
                    <h5 className="info-card-title">Additional Costs</h5>
                    <div className="info-grid">
                      <div className="info-item">
                        <span className="label">Buy4Me Cost:</span>
                        <span className="value">${order.buyForMeCost?.toFixed(2) || '0.00'}</span>
                      </div>
                      <div className="info-item">
                        <span className="label">Payment Method Cost:</span>
                        <span className="value">${order.paymentMethodCost?.toFixed(2) || '0.00'}</span>
                      </div>
                      <div className="info-item">
                        <span className="label">Additional Cost:</span>
                        <span className="value">${order.additionaCost?.toFixed(2) || '0.00'}</span>
                      </div>
                      <div className="info-item">
                        <span className="label">Tax:</span>
                        <span className="value">${order.tax?.toFixed(2) || '0.00'}</span>
                      </div>
                      <div className="info-item">
                        <span className="label">Merchant Delivery Fee:</span>
                        <span className="value">${order.merchentDeliveryFee?.toFixed(2) || '0.00'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Edit Order Tab */}
          {activeTab === 'edit' && hasPermission("Edit Order") && (
            <div className="tab-pane active">
              <div className="edit-order-form">
                <div className="row g-4">
                  {/* Basic Information */}
                  <div className="col-12">
                    <div className="form-section">
                      <h5 className="section-title">{t('common:basicInformation')}</h5>
                      <div className="row g-3">
                        <div className="col-md-6">
                          <label className="form-label">{t('common:productLink')}</label>
                          <input
                            type="url"
                            className="form-control"
                            name="link"
                            value={editFormData.link || ''}
                            onChange={handleEditInputChange}
                            placeholder={t('common:enterProductLink')}
                          />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">{t('common:merchantName')}</label>
                          <input
                            type="text"
                            className="form-control"
                            name="merchantName"
                            value={editFormData.merchantName || ''}
                            onChange={handleEditInputChange}
                            placeholder={t('common:enterMerchantName')}
                          />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">{t('common:description')}</label>
                          <input
                            type="text"
                            className="form-control"
                            name="description"
                            value={editFormData.description || ''}
                            onChange={handleEditInputChange}
                            placeholder={t('common:enterDescription')}
                          />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">{t('common:quantity')}</label>
                          <input
                            type="number"
                            className="form-control"
                            name="quantity"
                            value={editFormData.quantity || 1}
                            onChange={handleEditInputChange}
                            min="1"
                          />
                        </div>
                        <div className="col-12">
                          <label className="form-label">{t('common:extendedDescription')}</label>
                          <textarea
                            className="form-control"
                            name="extendedDescription"
                            value={editFormData.extendedDescription || ''}
                            onChange={handleEditInputChange}
                            rows={3}
                            placeholder={t('common:enterExtendedDescription')}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Dimensions & Weight */}
                  <div className="col-12">
                    <div className="form-section">
                      <h5 className="section-title">Dimensions & Weight</h5>
                      <div className="row g-3">
                        <div className="col-md-3">
                          <label className="form-label">Width (Inch)</label>
                          <input
                            type="number"
                            className="form-control"
                            name="width"
                            value={editFormData.width || 0}
                            onChange={handleEditInputChange}
                            step="0.01"
                          />
                        </div>
                        <div className="col-md-3">
                          <label className="form-label">Height (Inch)</label>
                          <input
                            type="number"
                            className="form-control"
                            name="height"
                            value={editFormData.height || 0}
                            onChange={handleEditInputChange}
                            step="0.01"
                          />
                        </div>
                        <div className="col-md-3">
                          <label className="form-label">Length (Inch)</label>
                          <input
                            type="number"
                            className="form-control"
                            name="length"
                            value={editFormData.length || 0}
                            onChange={handleEditInputChange}
                            step="0.01"
                          />
                        </div>
                        <div className="col-md-3">
                          <label className="form-label">Weight (LB)</label>
                          <input
                            type="number"
                            className="form-control"
                            name="weight"
                            value={editFormData.weight || 0}
                            onChange={handleEditInputChange}
                            step="0.01"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Pricing */}
                  <div className="col-12">
                    <div className="form-section">
                      <h5 className="section-title">Pricing</h5>
                      <div className="row g-3">
                        <div className="col-md-4">
                          <label className="form-label">Item Price ($)</label>
                          <input
                            type="number"
                            className="form-control"
                            name="itemPrice"
                            value={editFormData.itemPrice || 0}
                            onChange={handleEditInputChange}
                            step="0.01"
                          />
                        </div>
                        <div className="col-md-4">
                          <label className="form-label">Service Price ($)</label>
                          <input
                            type="number"
                            className="form-control"
                            name="servicePrice"
                            value={editFormData.servicePrice || 0}
                            onChange={handleEditInputChange}
                            step="0.01"
                          />
                        </div>
                        <div className="col-md-4">
                          <label className="form-label">Additional Cost ($)</label>
                          <input
                            type="number"
                            className="form-control"
                            name="additionaCost"
                            value={editFormData.additionaCost || 0}
                            onChange={handleEditInputChange}
                            step="0.01"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Dropdowns */}
                  <div className="col-12">
                    <div className="form-section">
                      <h5 className="section-title">Categories & Methods</h5>
                      <div className="row g-3">
                        <div className="col-md-6">
                          <label className="form-label">Category</label>
                          <select
                            className="form-select"
                            name="categoryId"
                            value={editFormData.categoryId || 0}
                            onChange={handleEditInputChange}
                          >
                            <option value={0}>Select Category</option>
                            {categories.map((category) => (
                              <option key={category.id} value={category.id}>
                                {category.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Payment Method</label>
                          <select
                            className="form-select"
                            name="paymentMethodId"
                            value={editFormData.paymentMethodId || 0}
                            onChange={handleEditInputChange}
                          >
                            <option value={0}>Select Payment Method</option>
                            {paymentMethods.map((method) => (
                              <option key={method.id} value={method.id}>
                                {method.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Shipping Insurance */}
                  <div className="col-12">
                    <div className="form-section">
                      <h5 className="section-title">Shipping Options</h5>
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          name="hasShippingInssurance"
                          checked={editFormData.hasShippingInssurance || false}
                          onChange={handleEditInputChange}
                          id="hasShippingInsurance"
                        />
                        <label className="form-check-label" htmlFor="hasShippingInsurance">
                          Shipment Insurance?
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="col-12">
                    <div className="d-flex gap-3 justify-content-end">
                      <ActionButton
                        variant="secondary"
                        onClick={() => setActiveTab('details')}
                      >
                        Cancel
                      </ActionButton>
                      <ActionButton
                        variant="primary"
                        onClick={handleUpdateOrder}
                        disabled={updating}
                      >
                        {updating ? 'Updating...' : 'Update Order'}
                      </ActionButton>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Status Update Tab */}
          {activeTab === 'status' && (
            <div className="tab-pane active">
              <div className="status-update-section">
                <h5>{t('tabs.statusUpdate')}</h5>
                <div className="current-status mb-4">
                  <span className="label">Current {t('fields.status')}:</span>
                  {getStatusBadge(order.orderStatus)}
                </div>

                <div className="status-form">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label">New Status</label>
                      <select
                        className="form-select"
                        name="orderStatus"
                        value={orderStatusForm.orderStatus}
                        onChange={handleStatusFormChange}
                      >
                        <option value={0}>Pending</option>
                        <option value={1}>Validated</option>
                        <option value={2}>Rejected</option>
                        <option value={3}>Valid For Shipping</option>
                        <option value={4}>Rejected By Shipping</option>
                        <option value={5}>Calculated</option>
                        <option value={6}>Approved</option>
                        <option value={7}>Approved By Customer</option>
                        <option value={8}>Rejected By Customer</option>
                        <option value={9}>Payment Under Collecting</option>
                        <option value={10}>Purchase Under Process</option>
                        <option value={11}>On Hold</option>
                        <option value={12}>Cancelled</option>
                        <option value={13}>Order Purchased</option>
                        <option value={14}>In Transit To Origin Warehouse</option>
                        <option value={15}>Received At Origin Warehouse</option>
                        <option value={16}>Lost/Damaged</option>
                        <option value={17}>Delayed</option>
                        <option value={18}>Refunded</option>
                        <option value={19}>Reordered</option>
                        <option value={20}>Shipped</option>
                        <option value={21}>Arrived at Destination Country</option>
                        <option value={22}>Under Customs Clearance</option>
                        <option value={23}>Released From Customs</option>
                        <option value={24}>Out For Delivery</option>
                        <option value={25}>Delivered</option>
                        <option value={26}>Delivery Failed</option>
                        <option value={27}>Out For Delivery (Rescheduled)</option>
                        <option value={28}>Returned To Sender</option>
                      </select>
                    </div>

                    {parseInt(orderStatusForm.orderStatus) === 18 && (
                      <div className="col-md-6">
                        <label className="form-label">Refund Type</label>
                        <select
                          className="form-select"
                          name="refundType"
                          value={orderStatusForm.refundType}
                          onChange={handleStatusFormChange}
                        >
                          <option value={0}>Cash Refund</option>
                          <option value={1}>Bank Transfer</option>
                          <option value={2}>Voucher</option>
                        </select>
                      </div>
                    )}

                    <div className="col-12">
                      <label className="form-label">Comment</label>
                      <textarea
                        className="form-control"
                        name="comment"
                        value={orderStatusForm.comment}
                        onChange={handleStatusFormChange}
                        rows={3}
                        placeholder="Enter status update comment..."
                      />
                    </div>

                    {/* Extra field based on status */}
                    {(() => {
                      const status = parseInt(orderStatusForm.orderStatus);
                      const paymentType = order?.paymentType;
                      let extraFieldLabel = null;

                      if (status === 10 && paymentType === 2) extraFieldLabel = t('extraFieldLabels.courierReceiptNo');
                      else if (status === 13) extraFieldLabel = t('extraFieldLabels.orderNumber');
                      else if (status === 20) extraFieldLabel = t('extraFieldLabels.shippingOrderNo');
                      else if (status === 25 && paymentType === 0) extraFieldLabel = t('extraFieldLabels.courierReceiptNo');
                      else if (status === 19) extraFieldLabel = t('extraFieldLabels.reorderNumber');
                      else if (status === 18) extraFieldLabel = t('extraFieldLabels.refundReceiptNo');

                      return extraFieldLabel && orderStatusForm.refundType != 2 ? (
                        <div className="col-md-6">
                          <label className="form-label">{extraFieldLabel}</label>
                          <input
                            type="text"
                            className="form-control"
                            name="extraField"
                            value={orderStatusForm.extraField}
                            onChange={handleStatusFormChange}
                            placeholder={`Enter ${extraFieldLabel.toLowerCase()}`}
                          />
                        </div>
                      ) : null;
                    })()}

                    <div className="col-12">
                      <div className="d-flex gap-3">
                        <ActionButton
                          variant="primary"
                          onClick={handleUpdateStatus}
                          disabled={updatingStatus}
                        >
                          {updatingStatus ? 'Updating...' : 'Update Status'}
                        </ActionButton>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Invoice Tab */}
          {activeTab === 'invoice' && (
            <div className="tab-pane active">
              <div className="invoice-section">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h5>{t('tabs.invoice')}</h5>
                  <ActionButton
                    variant="primary"
                    onClick={handlePrintInvoice}
                    icon={HiPrinter}
                  >
                    {t('actions.print')}
                  </ActionButton>
                </div>

                <div className="pricing-details">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <div className="pricing-card">
                        <h6>{t('sections.pricing')}</h6>
                        <div className="pricing-grid">
                          <div className="pricing-item">
                            <span className="label">{t('fields.itemPrice')}:</span>
                            <span className="value">${order.itemPrice?.toFixed(2) || '0.00'}</span>
                          </div>
                          <div className="pricing-item">
                            <span className="label">{t('fields.servicePrice')}:</span>
                            <span className="value">${order.servicePrice?.toFixed(2) || '0.00'}</span>
                          </div>
                          <div className="pricing-item">
                            <span className="label">{t('fields.discount')}:</span>
                            <span className="value text-success">-${order.discount?.toFixed(2) || '0.00'}</span>
                          </div>
                          <div className="pricing-item">
                            <span className="label">{t('fields.voucherValue')}:</span>
                            <span className="value text-success">-${order.voucherValue?.toFixed(2) || '0.00'}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="pricing-card">
                        <h6>{t('fields.totalPrice')}</h6>
                        <div className="pricing-grid">
                          <div className="pricing-item">
                            <span className="label">{t('fields.totalPrice')}:</span>
                            <span className="value">${order.totalPrice?.toFixed(2) || '0.00'}</span>
                          </div>
                          <div className="pricing-item">
                            <span className="label">{t('fields.totalPaid')}:</span>
                            <span className="value text-success">${order.totalPaid?.toFixed(2) || '0.00'}</span>
                          </div>
                          <div className="pricing-item total">
                            <span className="label">{t('fields.netDue')}:</span>
                            <span className="value text-primary fw-bold">
                              ${order.netDue?.toFixed(2) || '0.00'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Logs Tab */}
          {activeTab === 'logs' && (
            <div className="tab-pane active">
              <div className="logs-section">
                <h5>{t('tabs.logs')}</h5>
                {logs.length > 0 ? (
                  <div className="logs-list">
                    {logs.map((log) => (
                      <div key={log.id} className="log-item">
                        <div className="log-header">
                          <span className="log-action">{log.action}</span>
                          <span className="log-date">
                            {new Date(log.createdAt).toLocaleString()}
                          </span>
                        </div>
                        <div className="log-description">{log.description}</div>
                        <div className="log-user">By: {log.userName}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="alert alert-info">
                    No logs available for this order.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Summaries Tab */}
          {activeTab === 'summaries' && (
            <div className="tab-pane active">
              <div className="summaries-section">
                <h5>{t('tabs.summaries')}</h5>
                <div className="row g-4">
                  <div className="col-md-4">
                    <div className="summary-card">
                      <div className="summary-icon">
                        <HiChartBar />
                      </div>
                      <div className="summary-content">
                        <h6>Order Value</h6>
                        <div className="summary-value">${order.totalPrice?.toFixed(2) || '0.00'}</div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="summary-card">
                      <div className="summary-icon">
                        <HiDocumentText />
                      </div>
                      <div className="summary-content">
                        <h6>Items</h6>
                        <div className="summary-value">{order.quantity}</div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="summary-card">
                      <div className="summary-icon">
                        <HiClipboardList />
                      </div>
                      <div className="summary-content">
                        <h6>Status</h6>
                        <div className="summary-value">
                          {getStatusBadge(order.orderStatus)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </PageLayout>
  );
};
